<template>
    <div class="block-wrapper" :class="{ 'hide-content' : !isOpen, 'mobile': getMobileAppState }">
        <!-- Top Area Wrapper -->
        <div class="top-wrapper">
            <button-section
            removeBorder
            style="padding-top: 0;"
            :label="accountBlockTitle"
            :isOpen="!isOpen"
            @clicked="isOpen = !isOpen"/>
            <!-- Reset to initial values -->
            <button-secondary
            v-if="selectedProfileId === 2"
            icon="restart"
            buttonSize="small"
            :disabled="!account.id"
            :label="t('approvals.operators.accountsBlock.resetValues')"
            @clicked="emit('resetAccountValues')"/>
            <!-- Copy Values -->
            <button-secondary
            v-if="selectedProfileId !== 4"
            icon="copy"
            buttonSize="small"
            class="copy-button"
            :disabled="!account.id"
            :label="t('approvals.operators.accountsBlock.copyData')"
            @clicked="emit('duplicateAccountBlock')"/>
            <!-- Other Options -->
            <dropdown-more-options
            :options="moreOptions"
            @clicked="moreOptions.find(obj => obj.id === $event).callBackFunction()"/>
        </div>
        <!-- Account Dropdown -->
        <dropdown-account
        class="account-dropdown"
        :params="{
            placeholder: t('approvals.operators.accountsBlock.accountPlaceholder'),
            mandatory: true,
            headerType: 'balance-header',
            errorState: account.errorState,
            errorMessage: t('mandatoryFieldError')
        }"
        :options="accountsOptionsHandler"
        :selectedOption="account.id"
        @clicked="emit('accountSelected', $event)"/>

        <selection-controller-radio
        :id="account.id"
        :key="`radio_${account.id}`"
        :text="t('approvals.operators.accountsBlock.defaultAccountLabel')"
        :selected="account.id === operatorData.defaultAccountId.value"
        :disabled="!account.id"
        :errorState="operatorData.defaultAccountId.errorState && account.id"
        :errorMessage="t('mandatoryFieldError')"
        @clicked="emit('defaultAccount', $event)"/>
        <!-- No operations limits added not -->
        <infobox-generic v-if="!account.operationLimits.length && selectedProfileId !== 4" :label="t('approvals.operators.accountsBlock.infoBlock')"/>
        <!-- Table -->
        <table-with-horizontal-scroll
        v-if="account.operationLimits.length && selectedProfileId !== 4"
        noBoxStyles
        :tableIdentifier="`id${account.id}`"
        :hasDetail="false"
        :headerData="tableHeaderData"
        :tableStyles="tableStyles"
        :tableData="tableData"
        @dropdownClicked="emit('operationType', $event)"
        @inputChanged="emit('operationLimits', $event)"
        @buttonClicked="tableButtonClickHandler"/>
        <!-- Button to add limits -->
        <button-secondary
        v-if="account.operationLimits.length < getOperationsTypesList.length && selectedProfileId !== 4"
        buttonSize="small"
        class="add-limit"
        :label="t('approvals.operators.accountsBlock.addLimitsButton')"
        :disabled="!account.id"
        @clicked="emit('addOperationLimits')"/>
    </div>
</template>

<script setup>
import ButtonSection from '@/components/generic/buttons/Section'
import DropdownAccount from '@/components/generic/dropdowns/accounts/Account'
import SelectionControllerRadio from '@/components/generic/selectors/radio/Radio'
import InfoboxGeneric from '@/components/generic/infobox/Generic'
import ButtonSecondary from '@/components/generic/buttons/Secondary'
import TableWithHorizontalScroll from '@/components/generic/table/WithHorizontalScroll'
import DropdownMoreOptions from '@/components/generic/dropdowns/moreOptions/MoreOptions'

/* Vue & Assets */
import { ref, computed, onUpdated } from 'vue'
import { amountFormat } from '@/assets/scripts/amounts'
import { accountFormatHelper } from '@/assets/scripts/strings'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapActions & mapGetters */
const { getOperationsTypesList, getAccounts, getPlafondsAndLimits } = mapGetters(moduleMapper.APPROVALS_OPERATORS)
const { getMobileAppState } = mapGetters()

/* Emmitters */
const emit = defineEmits(['accountSelected', 'defaultAccount', 'addOperationLimits', 'operationType', 'operationLimits',
                        'deleteOperationRow', 'duplicateAccountBlock', 'removeAccountBlock', 'resetAccountValues', 'resetRowValues', 'accountBlockFinishUpdate'])

const props = defineProps({
    operatorData: Object,
    account: Object,
    cantRemove: Boolean,
    selectedProfileId: Number
})

const isOpen = ref(true)

const accountBlockTitle = computed(() => {
    if (!props.account.id) return ''

    return props.account.description === props.account.accountNumber ? accountFormatHelper(props.account.accountNumber) : `${props.account.description} - ${accountFormatHelper(props.account.accountNumber)}`
})

const accountsOptionsHandler = computed(() => {
    return getAccounts.value.bankAccountView.reduce((acc, account) => {
        if (!props.operatorData.accountIds.value.includes(account.id) || props.account.id === account.id) acc.push(account)
        return acc
    }, [])
})

const operationTypesHandler = (index) => {
    return getOperationsTypesList.value.reduce((acc, op) => {
        let validator = true
        props.account.operationLimits.forEach((limit, ind) => {
            if (index === ind) return
            if (limit.operationType.value === op.id) validator = false
        })

        if (validator) acc.push({ ...op, text: op.type })

        return acc
    }, [])
}

const operationLimitsHandler = (operation) => {
    return getPlafondsAndLimits.value.find(ele => ele.account.id === props.account.id)?.operationTypesWithPlafondsAndTransactionLimits.find(el => el.operationType.id === operation.operationType.value)?.plafondAndTransactionLimit
}

const tableHeaderData = [
	{ label: t('approvals.operators.accountsBlock.tableHeaders.operationType'), dataAttr: 'operationType', mandatory: true, showInMobile: true },
    { label: t('approvals.operators.accountsBlock.tableHeaders.plafond'), dataAttr: 'plafond', mandatory: true, showInMobile: true },
    { label: t('approvals.operators.accountsBlock.tableHeaders.transactionLimit'), dataAttr: 'transactionLimit', mandatory: true, showInMobile: true },
    { dataAttr: 'resetRowValues', rowCellType: 'button', showInMobile: true },
    { dataAttr: 'closeRow', rowCellType: 'button', showInMobile: true }
]

const tableStyles = [
    { width: '372px', 'min-width': '372px' },
    { width: '300px', 'min-width': '300px' },
    { width: '300px', 'min-width': '300px' },
    { padding: '0 0 14px 0' },
    { padding: '0 0 14px 0' }
]

const tableData = computed(() => {
	return props.account.operationLimits.map((operation, index) => {
		return [
            { value: index, dataAttr: 'id' },
            {
                type: 'dropdown',
                value: {
                    type: 'generic',
                    dropdownParams: {
                        rowsHaveMultipleLines: true,
                        errorState: operation.operationType.errorState && !operation.operationType.value,
                        errorMessage: t('mandatoryFieldError')
                    },
                    selectedOption: operation.operationType.value || '',
                    options: operationTypesHandler(index)
                },
                dataAttr: 'operationType'
            },
            {
                type: 'input',
                value: {
                    type: 'amount',
                    inputParams: {
                        currency: props.account.currency || '',
                        errorState: operation.plafond.errorState && !operation.plafond.value,
                        errorMessage: t('mandatoryFieldError'),
                        bottomMessage: operation.operationType.value && operationLimitsHandler(operation) && props.account.id
                                    ? t('approvals.operators.accountsBlock.tableData.amountBottomMessage', { amount: amountFormat(operationLimitsHandler(operation).plafond, props.account.currency ? props.account.currency : '') })
                                    : '',
                        maxValue: operation.operationType.value && operationLimitsHandler(operation) && props.account.id ? operationLimitsHandler(operation).plafond : ''
                    },
                    inputValue: operation.plafond.value ? operation.plafond.value : ''
                },
                dataAttr: 'plafond'
            },
            {
                type: 'input',
                value: {
                    type: 'amount',
                    inputParams: {
                        currency: props.account.currency || '',
                        errorState: operation.transactionLimit.errorState && !operation.transactionLimit.value,
                        errorMessage: t('mandatoryFieldError'),
                        bottomMessage: operation.operationType.value && operationLimitsHandler(operation) && props.account.id
                                    ? t('approvals.operators.accountsBlock.tableData.amountBottomMessage', { amount: amountFormat(operationLimitsHandler(operation).transactionLimit, props.account.currency ? props.account.currency : '') })
                                    : '',
                        maxValue: operation.operationType.value && operationLimitsHandler(operation) && props.account.id ? operationLimitsHandler(operation).transactionLimit : ''
                    },
                    inputValue: operation.transactionLimit.value ? operation.transactionLimit.value : ''
                },
                dataAttr: 'transactionLimit'
            },
            {
                type: 'button',
                value: {
                    iconName: 'restart',
                    variant: 'tertiary',
                    tooltipText: t('approvals.operators.accountsBlock.tooltipResetToMax')
                },
                dataAttr: 'resetRowValues'
            },
            { type: 'button', value: { iconName: 'close', variant: 'tertiary' }, dataAttr: 'closeRow' }
        ]
	})
})

const moreOptions = computed(() => {
    return [{
        id: 2,
        text: t('approvals.operators.accountsBlock.delete'),
        icon: 'delete',
		variant: 'negative',
        disabled: props.cantRemove,
        callBackFunction: () => { emit('removeAccountBlock') }
    }]
})

const tableButtonClickHandler = $event => {
    if ($event.dataAttr === 'closeRow') {
        emit('deleteOperationRow', $event.rowId)
    } else {
        emit('resetRowValues', $event.rowId)
    }
}

onUpdated(() => {
    emit('accountBlockFinishUpdate')
})
</script>

<style lang="scss" scoped>
.block-wrapper {
    width: 100%;
    padding-bottom: 16px;

    border-radius: 8px;
    border: 1px solid var(--secondary-light-2);

    display: flex;
    flex-direction: column;
    position: relative;

    &.hide-content {
        height: 58px;
        overflow: hidden;
    }

    .top-wrapper {
        width: 100%;
        padding: 8px 16px 0 16px;

        display: flex;
        gap: 8px;
    }

    .dropdown-wrapper.account-dropdown {
        width: calc(100% - 32px);
        margin: 0 auto;
    }

    .infobox-generic-wrapper, :deep(.radio-wrapper) {
        width: calc(100% - 32px);
        margin: 0 auto 16px auto;
    }

    .add-limit {
        width: fit-content;
        margin: 16px auto 0 auto;
    }

    &.mobile {
        .top-wrapper {
            flex-wrap: wrap;
            margin-bottom: 16px;

            :deep(.button-section) {
                width: calc(100% - 40px);
            }

            :deep(.dropdown-wrapper) {
                order: 2;
            }

            :deep(.button-secondary) {
                width: calc(100% - 142px);
                order: 3;

                &.copy-button {
                    width: 134px;
                }
            }
        }
    }
}
</style>
